import { useState } from 'react';
import { OrderDetail } from './OrderDetail';
import { Drawer } from '@allrouted/ui/Drawer';

/**
 * One order, opened from the board.
 *
 * The same pane the Orders screen uses -- its three tabs, its view-then-edit
 * behaviour, all of it -- rather than a second, thinner version of the same
 * information. A dispatcher checking why a visit is where it is should see the
 * order they would see anywhere else, not a summary that leaves out the field
 * they were looking for.
 *
 * Stacked over the route drawer that opened it, so Escape goes back to the route
 * rather than to the board.
 */
export function OrderDrawer({
  territoryId,
  orderId,
  orderName,
  vehicles,
  open,
  onClose,
  onSaved,
  onRemoved,
}: {
  territoryId: string;
  orderId: string | null;
  /** Known from the stop, so the title is right before the order has loaded. */
  orderName: string | null;
  vehicles: { id: string; name: string }[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Gone from the day: the drawer has nothing left to show. */
  onRemoved?: () => void;
}) {
  /* Bumped after a save so the pane reloads what it just wrote. */
  const [generation, setGeneration] = useState(0);

  if (!orderId) return null;

  return (
    <Drawer
      stacked
      open={open}
      title={orderName ?? 'Order'}
      subtitle="Order details"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <OrderDetail
        territoryId={territoryId}
        orderId={orderId}
        generation={generation}
        vehicles={vehicles}
        onSaved={() => {
          setGeneration((n) => n + 1);
          onSaved();
        }}
        /*
         * No map to drag a pin from in here, so the location is edited through
         * its own fields. Passing the map hooks nothing rather than pretending
         * there is a pin to move.
         */
        onAdjusting={() => {}}
        pinned={null}
        /*
         * Either removal leaves this drawer describing something that is no
         * longer on the route it was opened from, so it closes and the board
         * behind it reloads.
         */
        onRemoved={() => {
          onRemoved?.();
          onClose();
        }}
      />
    </Drawer>
  );
}
